import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { History as CardIconHistory } from 'lucide-react';
import {
  OCCURRENCE_SEVERITY_LABELS,
  OCCURRENCE_TYPE_LABELS,
  answerText,
  compareRecordVersions,
  questionsOf,
} from '@hue/contracts';
import { ChoiceChips, StatusText } from '@hue/design-system';
import type { ClientChart, SessionVisit } from '../../lib/api.js';
import { daysBetween, formatDate, formatHourMinute } from '../../lib/time-zone.js';
import { blocksOf, cn, materialLine } from './helpers.js';

export type TimelineFilter = 'all' | 'sessions' | 'records' | 'occurrences' | 'alerts';

type TimelineEvent =
  | { kind: 'session'; at: string; appointmentId: string }
  | { kind: 'record'; at: string; templateId: string; version: number }
  | { kind: 'signature'; at: string; documentId: string }
  | { kind: 'alert'; at: string; alertId: string }
  | { kind: 'alert-closed'; at: string; alertId: string };

const PREVIEW = 4;

/// A Linha do tempo junta as camadas do prontuário numa leitura só (ADR 0038, decisão 2). Não é
/// tabela: é o que as fichas e os alertas já guardam, em ordem do mais recente para o mais antigo.
/// Os documentos assinados — da ficha e do prontuário — entram junto das fichas, e cada atendimento
/// concluído entra como sessão, com o que foi registrado nela (ADR 0038, adendo de 2026-09-28).
export function Timeline({
  chart,
  timeZone,
  filter,
  full,
  onFilter,
  onFull,
  onOpenVersion,
}: {
  chart: ClientChart;
  timeZone: string;
  filter: TimelineFilter;
  full: boolean;
  onFilter: (filter: TimelineFilter) => void;
  onFull: (full: boolean) => void;
  onOpenVersion: (templateId: string, version: number) => void;
}) {
  const sessions = chart.sessions ?? [];
  const events: TimelineEvent[] = [
    ...sessions.map((visit) => ({
      kind: 'session' as const,
      at: visit.startsAt,
      appointmentId: visit.appointmentId,
    })),
    ...chart.records.flatMap((record) =>
      record.versions.map((version) => ({
        kind: 'record' as const,
        at: version.recordedAt,
        templateId: record.templateId,
        version: version.version,
      })),
    ),
    ...chart.documents.map((document) => ({
      kind: 'signature' as const,
      at: document.signedAt,
      documentId: document.id,
    })),
    ...chart.alerts.flatMap((alert) => [
      { kind: 'alert' as const, at: alert.createdAt, alertId: alert.id },
      ...(alert.closedAt
        ? [{ kind: 'alert-closed' as const, at: alert.closedAt, alertId: alert.id }]
        : []),
    ]),
  ]
    .filter((event) => {
      if (filter === 'all') return true;
      if (filter === 'sessions') return event.kind === 'session';
      if (filter === 'occurrences') {
        return (
          event.kind === 'session' &&
          Boolean(
            sessions.find((visit) => visit.appointmentId === event.appointmentId)?.session?.outcome
              ?.occurrence,
          )
        );
      }
      if (filter === 'records') return event.kind === 'record' || event.kind === 'signature';
      return event.kind === 'alert' || event.kind === 'alert-closed';
    })
    .sort((left, right) => right.at.localeCompare(left.at));

  return (
    <div className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-5 shadow-soft">
      <ContentCardHeader title="Linha do tempo" icon={<CardIconHistory className="size-4" />} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ChoiceChips
          label="O que mostrar"
          className="w-full min-w-0 sm:w-60"
          value={filter}
          options={[
            { value: 'all', label: 'Tudo' },
            ...(chart.sessions
              ? [
                  { value: 'sessions', label: 'Sessões' },
                  { value: 'occurrences', label: 'Intercorrências' },
                ]
              : []),
            { value: 'records', label: 'Fichas' },
            { value: 'alerts', label: 'Alertas' },
          ]}
          onChange={(value) => onFilter(value as typeof filter)}
        />
        <ChoiceChips
          label="Detalhe"
          className="w-full sm:w-44"
          value={full ? 'full' : 'summary'}
          options={[
            { value: 'summary', label: 'Resumido' },
            { value: 'full', label: 'Completo' },
          ]}
          onChange={(value) => onFull(value === 'full')}
        />
      </div>
      {events.length ? (
        <ol className="grid gap-0">
          {events.map((event) => (
            <li
              key={`${event.kind}-${eventKey(event)}`}
              className="grid grid-cols-[4.5rem_1rem_minmax(0,1fr)] gap-3"
            >
              <div className="pt-0.5 text-right text-xs tabular-nums text-content-muted">
                {formatDate(event.at, timeZone)}
                <br />
                <span className="text-content-faint">{formatHourMinute(event.at, timeZone)}</span>
              </div>
              <div className="relative flex justify-center">
                <span className="absolute inset-y-0 w-px bg-surface-border" aria-hidden="true" />
                <span
                  aria-hidden="true"
                  className={cn(
                    'relative mt-1.5 size-2.5 rounded-full',
                    event.kind === 'session' && 'bg-brand-500',
                    event.kind === 'record' && 'bg-info',
                    event.kind === 'signature' && 'bg-positive',
                    event.kind === 'alert' && 'bg-warning',
                    event.kind === 'alert-closed' && 'bg-content-faint',
                  )}
                />
              </div>
              <div className="min-w-0 pb-5">
                {event.kind === 'session' ? (
                  <SessionEvent
                    sessions={sessions}
                    appointmentId={event.appointmentId}
                    timeZone={timeZone}
                  />
                ) : event.kind === 'record' ? (
                  <RecordEvent
                    chart={chart}
                    templateId={event.templateId}
                    version={event.version}
                    full={full}
                    onFull={() => onFull(true)}
                    onOpen={() => onOpenVersion(event.templateId, event.version)}
                  />
                ) : event.kind === 'signature' ? (
                  <SignatureEvent
                    chart={chart}
                    documentId={event.documentId}
                    onOpenVersion={onOpenVersion}
                  />
                ) : (
                  <AlertEvent
                    chart={chart}
                    alertId={event.alertId}
                    closed={event.kind === 'alert-closed'}
                  />
                )}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-content-muted">
          Nada registrado ainda. A primeira ficha ou o primeiro alerta aparecem aqui.
        </p>
      )}
    </div>
  );
}

function RecordEvent({
  chart,
  templateId,
  version,
  full,
  onFull,
  onOpen,
}: {
  chart: ClientChart;
  templateId: string;
  version: number;
  full: boolean;
  onFull: () => void;
  onOpen: () => void;
}) {
  const record = chart.records.find((item) => item.templateId === templateId)!;
  const template = chart.templates.find((item) => item.id === templateId)!;
  const index = record.versions.findIndex((item) => item.version === version);
  const current = record.versions[index]!;
  const previous = record.versions[index - 1];
  const blocks = blocksOf(chart, current.templateVersionId);
  const changes = previous
    ? compareRecordVersions(
        { blocks: blocksOf(chart, previous.templateVersionId), answers: previous.answers },
        { blocks, answers: current.answers },
      )
    : [];
  const shown = full ? changes : changes.slice(0, PREVIEW);
  return (
    <div className="grid gap-1.5">
      <p className="text-sm font-semibold">
        {template.displayName} · versão {version}{' '}
        <span className="text-xs font-normal text-content-muted">
          por {current.recordedByDisplayName}
        </span>
      </p>
      {previous ? (
        changes.length ? (
          <ul className="grid gap-1 text-xs">
            {shown.map((change) => (
              <li key={change.questionId}>
                <span className="text-content-muted">{change.label}:</span>{' '}
                <span className="text-danger line-through decoration-danger/50">
                  {change.before ?? '—'}
                </span>{' '}
                → <strong className="text-positive">{change.after ?? '—'}</strong>
              </li>
            ))}
            {changes.length > shown.length ? (
              <li>
                <button
                  type="button"
                  onClick={onFull}
                  className="cursor-pointer font-semibold text-brand-400 hover:underline"
                >
                  +{changes.length - shown.length}{' '}
                  {changes.length - shown.length === 1 ? 'mudança' : 'mudanças'} · ver completo
                </button>
              </li>
            ) : null}
          </ul>
        ) : (
          <span className="text-xs text-content-muted">Gravada sem mudar respostas.</span>
        )
      ) : full ? (
        <ul className="grid gap-1 text-xs">
          <li className="text-content-muted">Primeiro preenchimento:</li>
          {questionsOf(blocks).flatMap((question) => {
            const text = answerText(question, current.answers[question.id]);
            return text === null
              ? []
              : [
                  <li key={question.id}>
                    <span className="text-content-muted">{question.label}:</span>{' '}
                    <strong>{text}</strong>
                  </li>,
                ];
          })}
        </ul>
      ) : (
        <span className="text-xs text-content-muted">Primeiro preenchimento.</span>
      )}
      <button
        type="button"
        onClick={onOpen}
        className="w-fit cursor-pointer text-xs font-semibold text-brand-400 hover:underline"
      >
        Ver esta versão
      </button>
    </div>
  );
}

function SignatureEvent({
  chart,
  documentId,
  onOpenVersion,
}: {
  chart: ClientChart;
  documentId: string;
  onOpenVersion: (templateId: string, version: number) => void;
}) {
  const document = chart.documents.find((item) => item.id === documentId)!;
  const record = chart.records.find((item) => item.id === document.recordId);
  const version = record?.versions.find((item) => item.id === document.versionIds[0]);
  const template = chart.templates.find((item) => item.id === record?.templateId);
  const what =
    document.kind === 'CHART'
      ? 'Prontuário'
      : `${template?.displayName ?? 'Ficha'}${version ? ` · versão ${String(version.version)}` : ''}`;
  return (
    <div className="grid gap-1">
      <p className="text-sm font-semibold">
        {what} {document.method === 'DIGITAL' ? 'assinado na tela' : 'com via física anexada'}
      </p>
      <p className="text-xs text-content-muted">
        Colhido por {document.collectedByDisplayName}
        {document.professionalDisplayName ? ` · com ${document.professionalDisplayName}` : ''}
        {document.file ? ` · ${document.file.fileName}` : ''}
      </p>
      {record && version ? (
        <button
          type="button"
          onClick={() => onOpenVersion(record.templateId, version.version)}
          className="w-fit cursor-pointer text-xs font-semibold text-brand-400 hover:underline"
        >
          Ver esta versão
        </button>
      ) : null}
    </div>
  );
}

function AlertEvent({
  chart,
  alertId,
  closed,
}: {
  chart: ClientChart;
  alertId: string;
  closed: boolean;
}) {
  const alert = chart.alerts.find((item) => item.id === alertId)!;
  return closed ? (
    <div className="grid gap-1">
      <p className="text-sm font-semibold">
        Alerta encerrado{' '}
        <span className="text-xs font-normal text-content-muted">
          por {alert.closedByDisplayName}
        </span>
      </p>
      <p className="text-xs">
        <s className="text-content-muted">{alert.text}</s> — {alert.closeReason}
      </p>
    </div>
  ) : (
    <div className="grid gap-1">
      <p className="text-sm font-semibold">
        Alerta criado{' '}
        <span className="text-xs font-normal text-content-muted">
          por {alert.createdByDisplayName}
        </span>
      </p>
      <p className="text-xs">{alert.text}</p>
    </div>
  );
}

function eventKey(event: TimelineEvent): string {
  if (event.kind === 'session') return event.appointmentId;
  if ('alertId' in event) return event.alertId;
  if ('documentId' in event) return event.documentId;
  return `${event.templateId}-${String(event.version)}`;
}

/// Um atendimento concluído: o que foi usado e o que foi registrado na sessão.
function SessionEvent({
  sessions,
  appointmentId,
  timeZone,
}: {
  sessions: readonly SessionVisit[];
  appointmentId: string;
  timeZone: string;
}) {
  const index = sessions.findIndex((visit) => visit.appointmentId === appointmentId);
  const visit = sessions[index]!;
  // A lista vem do mais recente ao mais antigo: a anterior é a próxima da lista.
  const previous = sessions[index + 1];
  const days = previous ? daysBetween(previous.startsAt, visit.startsAt, timeZone) : null;
  const outcome = visit.session?.outcome;
  const occurrence = outcome?.occurrence;
  return (
    <div className="grid gap-1.5">
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-semibold">
        {visit.serviceDisplayName} com {visit.professionalDisplayName}
        <span className="text-xs font-normal text-content-muted">
          {days === null ? 'primeira sessão registrada' : `${String(days)} dias depois da anterior`}
        </span>
        {occurrence ? (
          <StatusText tone="danger">
            {OCCURRENCE_TYPE_LABELS[occurrence.type]} ·{' '}
            {OCCURRENCE_SEVERITY_LABELS[occurrence.severity]}
          </StatusText>
        ) : null}
      </p>
      <SessionFacts visit={visit} />
    </div>
  );
}

/// O que se sabe de uma sessão, em pares de rótulo e valor: protocolo, material, checagem,
/// anotações, intercorrência e destaque. Só aparece o que foi registrado.
export function SessionFacts({
  visit,
  withHighlight = true,
}: {
  visit: SessionVisit;
  /// No "Antes de começar" o destaque tem aviso próprio e não se repete na lista.
  withHighlight?: boolean;
}) {
  const session = visit.session;
  const occurrence = session?.outcome?.occurrence;
  const rows: [string, string][] = [
    ...(visit.protocol ? [['Protocolo', visit.protocol] as [string, string]] : []),
    ...(visit.materials?.length
      ? [['Material', visit.materials.map(materialLine).join(' · ')] as [string, string]]
      : []),
    ...(visit.checkLines.length
      ? [
          [
            'Checagem',
            visit.checkLines.map((line) => `${line.label} ${line.answer}`).join(' · '),
          ] as [string, string],
        ]
      : []),
    ...(session?.check?.notes ? [['Anotações', session.check.notes] as [string, string]] : []),
    ...(occurrence
      ? [
          [
            'Intercorrência',
            [
              occurrence.description,
              occurrence.products.length
                ? `Produto: ${occurrence.products.map((product) => product.displayName).join(', ')}`
                : null,
              occurrence.conduct ? `Conduta: ${occurrence.conduct}` : null,
            ]
              .filter(Boolean)
              .join(' · '),
          ] as [string, string],
        ]
      : []),
    ...(withHighlight && session?.outcome?.highlight
      ? [['Destaque', session.outcome.highlight] as [string, string]]
      : []),
  ];
  if (!rows.length) {
    return <p className="text-xs text-content-muted">Concluído sem registro da sessão.</p>;
  }
  return (
    <dl className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-content-muted">{label}</dt>
          <dd className="m-0 min-w-0 break-words text-content-secondary">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
